import { useCallback, useEffect, useState } from 'react';
import { isView, type View } from './navModel';

export interface Route {
  view: View;
  /** optional focus/tab segment (a deal id, a settings sub-tab, …); parsed for a future record-deep-link
   *  step but not yet set by the shell. */
  arg: string;
}

function parseHash(fallback: View): Route {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const [v = '', arg = ''] = raw.split('/');
  return { view: isView(v) ? v : fallback, arg: decodeURIComponent(arg) };
}

/**
 * Minimal hash-based route state (CW-11). The current VIEW lives in the URL (`#/deals`), so a REFRESH keeps
 * the user where they were (not dumped back to their landing screen), the browser Back/Forward buttons work,
 * and a screen is bookmarkable/shareable — none of which the old in-memory `view` useState allowed. No router
 * dependency; a bare hashchange listener is enough. `navigate(view)` pushes a history entry (use `replace` to
 * swap without one). The `arg` segment is parsed so a later step can deep-link a specific record.
 */
export function useHashRoute(fallback: View): {
  view: View;
  arg: string;
  navigate: (view: View, arg?: string, opts?: { replace?: boolean }) => void;
} {
  const [route, setRoute] = useState<Route>(() => parseHash(fallback));

  useEffect(() => {
    // Reflect the initial view in the URL from the first render, so even a bare load is bookmarkable.
    if (!window.location.hash) window.history.replaceState(null, '', `#/${route.view}`);
    const onHash = () => setRoute(parseHash(fallback));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // Runs once: `fallback` is stable for the shell's lifetime and the listener reads the live hash.
  }, []);

  const navigate = useCallback((view: View, arg = '', opts?: { replace?: boolean }) => {
    const hash = arg ? `#/${view}/${encodeURIComponent(arg)}` : `#/${view}`;
    if (opts?.replace) window.history.replaceState(null, '', hash);
    else window.location.hash = hash; // pushes a history entry + fires hashchange (no-op if unchanged)
    setRoute({ view, arg });
  }, []);

  return { view: route.view, arg: route.arg, navigate };
}
